import React from 'react';
import type { Edge, GraphLayout, StepNode } from './types';

import IoCard from './graph/IoCard';
import { getNodeStatusStyles } from './graph/utils';
import { useSyncedHorizontalScroll } from './graph/hooks/useSyncedHorizontalScroll';
import { useWiringPaths } from './graph/hooks/useWiringPaths';
import { getStatusTextClass, isRunningStatus, toStatusLabel, vumeterBorderClass, vumeterTextClass } from '@/utils/runStatusUi';

interface Props {
  nodes: StepNode[];
  edges: Edge[];
  selectedStepId: string;
  selectedStepLabel?: string;
  enableRunState?: boolean;
  statusByStepId?: Record<string, string | null | undefined>;
  runtimeInputs?: Record<string, any>;
  runtimeStepById?: Record<string, { status?: string | null; last_error?: string | null; output?: any; job_id?: any; job_type?: any; attempts?: number | null; updated_at?: string | null }>;
  stepProgressById?: Record<string, { pct?: number | null; message?: string | null } | null | undefined>;
  /** Optional: last progress timestamp per step (ms since epoch). Used for “updated/no progress” while snapshots may be stale. */
  stepProgressUpdatedAtMsById?: Record<string, number | null | undefined>;
  /** Optional: recent formatted progress lines per step for live-running nodes (best-effort). */
  stepProgressLinesById?: Record<string, string[] | null | undefined>;
  layout: GraphLayout;
  orientation?: 'horizontal' | 'vertical';
  inputKeys: string[];
  inputMetaByKey?: Record<string, { type?: string; description?: string }>;
  outputMetaByKey?: Record<string, { type?: string; description?: string }>;
  outputKeys: string[];
  outputsBindingsJson: string;
  stepOutputKeysByStepId?: Record<string, string[]>;
  onSelect: (stepId: string) => void;
}

export default function WorkflowGraphView({ nodes, edges, selectedStepId, selectedStepLabel, enableRunState = false, statusByStepId, runtimeInputs, runtimeStepById, stepProgressById, stepProgressUpdatedAtMsById, stepProgressLinesById, layout, orientation = 'horizontal', inputKeys, inputMetaByKey, outputMetaByKey, outputKeys, outputsBindingsJson, stepOutputKeysByStepId, onSelect }: Props) {
  const [nowTickMs, setNowTickMs] = React.useState<number>(() => Date.now());

  const parseDateMs = React.useCallback((v: any): number | null => {
    const s = String(v ?? '').trim();
    if (!s) return null;
    const ms = Date.parse(s);
    return Number.isFinite(ms) ? ms : null;
  }, []);

  const formatAge = React.useCallback((ageMs: number): string => {
    const s = Math.max(0, Math.floor(ageMs / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 48) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  }, []);
  const getWhenBadge = React.useCallback((n: StepNode): { show: boolean; title: string; has: string[]; hasAny: string[] } => {
    const when = n?.def?.when;
    const has = (when?.has ?? []).map((s) => String(s ?? '').trim()).filter(Boolean);
    const hasAny = (when?.has_any ?? []).map((s) => String(s ?? '').trim()).filter(Boolean);
    const parts: string[] = [];
    if (has.length) parts.push(`has: ${has.join(', ')}`);
    if (hasAny.length) parts.push(`has_any: ${hasAny.join(', ')}`);
    return {
      show: parts.length > 0,
      title: parts.length ? `Run condition\n${parts.join('\n')}` : '',
      has,
      hasAny,
    };
  }, []);

  const nodeById = React.useMemo(() => {
    return new Map(nodes.map((n) => [n.id, n] as const));
  }, [nodes]);

  const whenLabelByEdgeId = React.useMemo(() => {
    const map = new Map<string, string>();
    edges.forEach((e) => {
      const toNode = nodeById.get(e.to);
      if (!toNode) return;
      const when = getWhenBadge(toNode);
      if (!when.show) return;

      const pick = (arr: string[]) => {
        const shown = arr.slice(0, 1);
        const extra = Math.max(0, arr.length - shown.length);
        if (!shown.length) return '';
        return extra > 0 ? `${shown[0]} +${extra}` : shown[0];
      };

      const label = when.has.length
        ? `◆ ${pick(when.has)}`
        : (when.hasAny.length ? `◆ any: ${pick(when.hasAny)}` : '◆ when');
      map.set(e.id, label);
    });
    return map;
  }, [edges, getWhenBadge, nodeById]);

  const whenHasByStepId = React.useMemo(() => {
    const map = new Map<string, Set<string>>();
    nodes.forEach((n) => {
      const when = getWhenBadge(n);
      if (!when.show || when.has.length === 0) return;
      map.set(n.id, new Set(when.has));
    });
    return map;
  }, [getWhenBadge, nodes]);

  const tryParseJsonObject = React.useCallback((text: string): Record<string, unknown> => {
    try {
      const parsed = JSON.parse(text);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }, []);

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const leftScrollRef = React.useRef<HTMLDivElement | null>(null);
  const centerScrollRef = React.useRef<HTMLDivElement | null>(null);
  const rightScrollRef = React.useRef<HTMLDivElement | null>(null);
  const inputElByKeyRef = React.useRef<Map<string, HTMLDivElement | null>>(new Map());
  const outputElByKeyRef = React.useRef<Map<string, HTMLDivElement | null>>(new Map());
  const stepElByIdRef = React.useRef<Map<string, HTMLButtonElement | null>>(new Map());

  const runningSinceMsByStepIdRef = React.useRef<Map<string, number>>(new Map());
  const completedDurationMsByStepIdRef = React.useRef<Map<string, number>>(new Map());
  const prevStatusByStepIdRef = React.useRef<Map<string, string>>(new Map());

  const progressLogByStepIdRef = React.useRef<Map<string, string[]>>(new Map());
  const prevProgressLineByStepIdRef = React.useRef<Map<string, string>>(new Map());

  React.useEffect(() => {
    if (!enableRunState) return;
    const t = setInterval(() => setNowTickMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [enableRunState]);

  const gutterW = 0;
  const padding = Number.isFinite(layout.padding) ? layout.padding : 16;
  const colWidth = Number.isFinite(layout.colWidth) ? layout.colWidth : 220;

  const canvasW = layout.width + gutterW;
  const canvasH = layout.height;

  const nodesByLevel = React.useMemo(() => {
    const entries: Array<{ id: string; level: number; x: number }> = nodes.map((n) => {
      const pos = layout.positions[n.id];
      return {
        id: n.id,
        level: Number.isFinite(pos?.level) ? (pos!.level as number) : 0,
        x: Number.isFinite(pos?.x) ? (pos!.x as number) : 0,
      };
    });

    const byId = new Map(nodes.map((n) => [n.id, n] as const));
    const maxLevel = Math.max(0, ...entries.map((e) => e.level));
    const levels: Array<{ level: number; nodes: StepNode[] }> = [];

    for (let l = 0; l <= maxLevel; l++) {
      const ids = entries
        .filter((e) => e.level === l)
        .sort((a, b) => (a.x !== b.x ? a.x - b.x : a.id.localeCompare(b.id)))
        .map((e) => e.id);

      levels.push({
        level: l,
        nodes: ids.map((id) => byId.get(id)).filter(Boolean) as StepNode[],
      });
    }

    return levels;
  }, [layout.positions, nodes]);

  const usedInputsByStepId = React.useMemo(() => {
    const map = new Map<string, Set<string>>();
    nodes.forEach((n) => {
      const obj = tryParseJsonObject(n.paramsBindingsJson);
      Object.values(obj).forEach((v) => {
        if (typeof v !== 'string') return;

        const tpl = /\{\{\s*([\s\S]*?)\s*\}\}/g;
        let t: RegExpExecArray | null;
        while ((t = tpl.exec(v)) !== null) {
          const inner = (t[1] ?? '').trim();
          if (!inner) continue;
          const re = /\binputs\.([a-zA-Z0-9_-]+)\b/g;
          let m: RegExpExecArray | null;
          while ((m = re.exec(inner)) !== null) {
            const key = m[1];
            if (!key) continue;
            let set = map.get(n.id);
            if (!set) {
              set = new Set<string>();
              map.set(n.id, set);
            }
            set.add(key);
          }
        }
      });
    });
    return map;
  }, [nodes, tryParseJsonObject]);

  const wiredOutputs = React.useMemo(() => {
    const rx = /^\{\{\s*steps\.([a-zA-Z0-9_-]+)\.outputs\.([a-zA-Z0-9_-]+)\s*\}\}$/;
    const obj = tryParseJsonObject(outputsBindingsJson);
    const pairs: Array<{ outKey: string; stepId: string; outputKey: string }> = [];
    Object.entries(obj).forEach(([outKey, v]) => {
      if (typeof v !== 'string') return;
      const m = v.trim().match(rx);
      if (!m) return;
      pairs.push({ outKey, stepId: m[1], outputKey: m[2] });
    });
    pairs.sort((a, b) => (a.stepId === b.stepId ? a.outKey.localeCompare(b.outKey) : a.stepId.localeCompare(b.stepId)));
    return pairs;
  }, [outputsBindingsJson, tryParseJsonObject]);

  const { edgePaths, edgeMidpoints, inputWiringPaths, inputWiringMidpoints, outputWiringPaths, scheduleWiringUpdate } = useWiringPaths({
    orientation,
    nodes,
    edges,
    usedInputsByStepId,
    wiredOutputs,
    containerRef,
    inputElByKeyRef,
    outputElByKeyRef,
    stepElByIdRef,
  });

  React.useEffect(() => {
    scheduleWiringUpdate();
  }, [scheduleWiringUpdate, inputKeys, outputKeys, selectedStepId, orientation]);

  const toCompactValue = React.useCallback((v: unknown): string => {
    const truncate = (s: string, maxLen = 160): string => {
      const flat = s.replace(/\s+/g, ' ').trim();
      if (flat.length <= maxLen) return flat;
      return `${flat.slice(0, maxLen)}…`;
    };

    if (typeof v === 'string') return truncate(v);
    try {
      return truncate(JSON.stringify(v));
    } catch {
      return truncate(String(v));
    }
  }, []);

  const normalizeRuntimeOutput = React.useCallback((out: any): any => {
    // Some workers / proxies may double-encode JSON or wrap output.
    // Normalize to the most useful object for both template resolution and display.
    if (out == null) return out;

    // Handle JSON-string outputs.
    if (typeof out === 'string') {
      const text = out.trim();
      if ((text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))) {
        try {
          return normalizeRuntimeOutput(JSON.parse(text));
        } catch {
          return out;
        }
      }
      return out;
    }

    if (typeof out !== 'object') return out;

    // Common wrapper: { output: {...} }
    if (!Array.isArray(out) && (out as any).output !== undefined) {
      const inner = (out as any).output;
      if (inner && typeof inner === 'object') return normalizeRuntimeOutput(inner);
    }

    return out;
  }, []);

  const getRuntimeOutputValue = React.useCallback((out: any, outKey: string): any => {
    const base = normalizeRuntimeOutput(out);
    if (!base || typeof base !== 'object' || Array.isArray(base)) return undefined;
    const obj: any = base;

    // Support nested result objects like { result: { foo: 1 } }
    if (obj.result && typeof obj.result === 'object' && obj.result !== null && !Array.isArray(obj.result)) {
      if (Object.prototype.hasOwnProperty.call(obj.result, outKey)) return obj.result[outKey];
    }

    // Support scalar result via {{steps.*.outputs.result}}
    if (outKey === 'result' && Object.prototype.hasOwnProperty.call(obj, 'result')) {
      return obj.result;
    }

    if (Object.prototype.hasOwnProperty.call(obj, outKey)) return obj[outKey];
    return undefined;
  }, [normalizeRuntimeOutput]);

  const extractDurationMs = React.useCallback((out: any): number | null => {
    const base = normalizeRuntimeOutput(out);
    if (base == null) return null;
    if (typeof base === 'number' && Number.isFinite(base)) return base;
    if (typeof base === 'string') {
      const n = Number(base.trim());
      return Number.isFinite(n) ? n : null;
    }
    if (typeof base !== 'object') return null;

    const o: any = base;
    const pickNum = (v: any): number | null => {
      if (typeof v === 'number' && Number.isFinite(v)) return v;
      if (typeof v === 'string') {
        const n = Number(v.trim());
        return Number.isFinite(n) ? n : null;
      }
      return null;
    };

    const direct = pickNum(o.duration_ms ?? o.durationMs ?? o.DurationMs);
    if (direct != null) return direct;

    const meta = o.meta && typeof o.meta === 'object' ? pickNum(o.meta.duration_ms ?? o.meta.durationMs ?? o.meta.DurationMs) : null;
    if (meta != null) return meta;

    const res = o.result && typeof o.result === 'object' ? pickNum(o.result.duration_ms ?? o.result.durationMs ?? o.result.DurationMs) : null;
    if (res != null) return res;

    if (o.output != null) return extractDurationMs(o.output);
    return null;
  }, [normalizeRuntimeOutput]);

  const formatMsCompact = React.useCallback((ms: number): string => {
    if (!Number.isFinite(ms as any)) return '—';
    const v = Math.max(0, Number(ms));
    if (v < 1000) return `${Math.round(v)}ms`;
    const s = v / 1000;
    if (s < 60) return `${s.toFixed(s < 10 ? 2 : 1)}s`;
    const m = Math.floor(s / 60);
    const rs = Math.round(s % 60);
    return `${m}m ${rs}s`;
  }, []);

  const discardedByStepId = React.useMemo(() => {
    if (!enableRunState) return new Map<string, boolean>();
    if (!runtimeInputs || typeof runtimeInputs !== 'object') return new Map<string, boolean>();

    const getByDotPath = (obj: any, path: string): any => {
      const parts = String(path ?? '')
        .split('.')
        .map((s) => s.trim())
        .filter(Boolean);
      let cur: any = obj;
      for (const p of parts) {
        if (!cur || typeof cur !== 'object') return undefined;
        if (!Object.prototype.hasOwnProperty.call(cur, p)) return undefined;
        cur = cur[p];
      }
      return cur;
    };

    const isFilled = (v: any): boolean => {
      if (v === null || v === undefined) return false;
      if (typeof v === 'string') return v.trim().length > 0;
      return true;
    };

    const isWhenSatisfied = (when: any): boolean => {
      if (!when || typeof when !== 'object') return true;
      const has: string[] = Array.isArray(when.has) ? when.has : [];
      const hasAny: string[] = Array.isArray(when.has_any) ? when.has_any : [];
      if (has.length === 0 && hasAny.length === 0) return true;

      const okAll = has.every((k) => isFilled(getByDotPath(runtimeInputs as any, k)));
      if (!okAll) return false;
      if (hasAny.length === 0) return true;
      return hasAny.some((k) => isFilled(getByDotPath(runtimeInputs as any, k)));
    };

    const isNonTerminalUnstarted = (statusRaw: any): boolean => {
      const s = String(statusRaw ?? '').trim().toUpperCase();
      if (s === '') return true;
      return s === 'PENDING' || s === 'READY';
    };

    // 1) Steps whose when is not satisfied (and which are not already running/completed).
    const excluded = new Set<string>();
    for (const n of nodes) {
      const stepId = n.id;
      const statusRaw = statusByStepId?.[stepId];
      if (!n?.def?.when) continue;
      if (isWhenSatisfied(n.def.when)) continue;
      if (!isNonTerminalUnstarted(statusRaw)) continue;
      excluded.add(stepId);
    }

    // 2) Any downstream step of an excluded step is also excluded.
    // Only apply this to steps without an explicit runtime status.
    if (excluded.size > 0 && Array.isArray(edges) && edges.length > 0) {
      const out = new Map<string, string[]>();
      for (const e of edges) {
        if (!e?.from || !e?.to) continue;
        if (!out.has(e.from)) out.set(e.from, []);
        out.get(e.from)!.push(e.to);
      }

      const queue = Array.from(excluded);
      while (queue.length > 0) {
        const cur = queue.shift()!;
        const next = out.get(cur) ?? [];
        for (const dst of next) {
          const statusRaw = statusByStepId?.[dst];
          if (!isNonTerminalUnstarted(statusRaw)) continue;
          if (!excluded.has(dst)) {
            excluded.add(dst);
            queue.push(dst);
          }
        }
      }
    }

    const m = new Map<string, boolean>();
    excluded.forEach((id) => {
      m.set(id, true);
    });
    return m;
  }, [enableRunState, runtimeInputs, nodes, edges, statusByStepId]);

  const getStatusLabelForStepId = React.useCallback(
    (stepId: string): string => {
      const raw = statusByStepId?.[stepId];
      const s = String(raw ?? '').trim().toUpperCase();
      const isTerminalOrRunning = s === 'RUNNING' || s === 'IN_PROGRESS' || s === 'STARTED' || s === 'SUCCEEDED' || s === 'DONE' || s === 'FAILED' || s === 'ERROR';
      if (!isTerminalOrRunning && discardedByStepId.get(stepId)) return 'DISCARDED';
      return toStatusLabel(raw, 'PENDING');
    },
    [statusByStepId, discardedByStepId],
  );

  React.useEffect(() => {
    if (!enableRunState) return;
    const now = Date.now();

    for (const n of nodes) {
      const stepId = n.id;
      const statusLabel = getStatusLabelForStepId(stepId);
      const prev = prevStatusByStepIdRef.current.get(stepId);
      prevStatusByStepIdRef.current.set(stepId, statusLabel);

      const isRunning = isRunningStatus(statusLabel);
      const runtime = runtimeStepById?.[stepId];
      const durFromOutput = extractDurationMs(runtime?.output);

      if (isRunning) {
        if (!runningSinceMsByStepIdRef.current.has(stepId)) {
          runningSinceMsByStepIdRef.current.set(stepId, now);
        }
        // If a run restarts/retries into running, clear any stale completed duration.
        completedDurationMsByStepIdRef.current.delete(stepId);
        continue;
      }

      if (durFromOutput != null) {
        completedDurationMsByStepIdRef.current.set(stepId, durFromOutput);
        runningSinceMsByStepIdRef.current.delete(stepId);
        continue;
      }

      // If we observed a transition from running -> not running, freeze elapsed.
      const wasRunning = isRunningStatus(prev);
      if (wasRunning) {
        const started = runningSinceMsByStepIdRef.current.get(stepId);
        if (started != null) {
          completedDurationMsByStepIdRef.current.set(stepId, Math.max(0, now - started));
        }
        runningSinceMsByStepIdRef.current.delete(stepId);
      }
    }
  }, [enableRunState, nodes, getStatusLabelForStepId, runtimeStepById, extractDurationMs]);

  React.useEffect(() => {
    if (!enableRunState) return;

    const formatProgressLine = (p: any): string => {
      if (!p) return '';
      const pct = (typeof p.pct === 'number' ? p.pct : (typeof p.percent === 'number' ? p.percent : null));
      const msg = typeof p.message === 'string' ? p.message.trim() : '';
      if (pct != null && msg) return `${pct}% ${msg}`;
      if (pct != null) return `${pct}%`;
      if (msg) return msg;
      return '';
    };

    for (const n of nodes) {
      const stepId = n.id;
      const statusLabel = getStatusLabelForStepId(stepId);
      const isRunning = isRunningStatus(statusLabel);
      if (!isRunning) continue;

      const line = formatProgressLine(stepProgressById?.[stepId] as any);
      if (!line) continue;

      const prev = prevProgressLineByStepIdRef.current.get(stepId);
      if (prev === line) continue;
      prevProgressLineByStepIdRef.current.set(stepId, line);

      const existing = progressLogByStepIdRef.current.get(stepId) ?? [];
      const next = [...existing, line];
      const trimmed = next.slice(-1);
      progressLogByStepIdRef.current.set(stepId, trimmed);
    }
  }, [enableRunState, nodes, getStatusLabelForStepId, stepProgressById]);

  const resolveTemplateValue = React.useCallback((v: unknown): unknown => {
    if (!enableRunState) return v;
    if (typeof v !== 'string') return v;
    const text = v.trim();

    // Best-effort: if this is a template that references a runtime input, preview its current value.
    // (We intentionally do not attempt to emulate transforms here.)
    const tpl = text.match(/^\{\{\s*([\s\S]*?)\s*\}\}$/);
    if (tpl) {
      const inner = (tpl[1] ?? '').trim();
      const m = inner.match(/\binputs\.([a-zA-Z0-9_-]+)\b/);
      const key = m?.[1];
      if (key && runtimeInputs && Object.prototype.hasOwnProperty.call(runtimeInputs, key)) {
        return (runtimeInputs as any)[key];
      }
    }

    const mStepOut = text.match(/^\{\{\s*steps\.([a-zA-Z0-9_-]+)\.outputs\.([a-zA-Z0-9_-]+)\s*\}\}$/);
    if (mStepOut) {
      const stepId = mStepOut[1];
      const outKey = mStepOut[2];
      const step = runtimeStepById?.[stepId];
      const out = step?.output;
      const resolved = getRuntimeOutputValue(out, outKey);
      if (resolved !== undefined) return resolved;
      return v;
    }

    return v;
  }, [enableRunState, getRuntimeOutputValue, runtimeInputs, runtimeStepById]);

  const toKvLines = React.useCallback((obj: any): string[] => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
    return Object.entries(obj)
      .sort(([a], [b]) => String(a).localeCompare(String(b)))
      .map(([k, val]) => `${String(k)}: ${toCompactValue(val)}`);
  }, [toCompactValue]);

  const getStepIoPreview = React.useCallback((n: StepNode): {
    inLines: string[];
    inTitle: string;
    outLines: string[];
    outTitle: string;
  } => {
    const bindings = tryParseJsonObject(n.paramsBindingsJson);
    const defaults = tryParseJsonObject(n.paramsDefaultsJson);
    const keys = Array.from(new Set([...Object.keys(bindings), ...Object.keys(defaults)])).sort();

    const items = keys.map((k) => {
      const hasBinding = Object.prototype.hasOwnProperty.call(bindings, k);
      const hasDefault = Object.prototype.hasOwnProperty.call(defaults, k);
      const rawVal = hasBinding ? (bindings as any)[k] : (hasDefault ? (defaults as any)[k] : '');
      const val = toCompactValue(resolveTemplateValue(rawVal));
      return `${k}: ${val}`;
    });

    const inTitle = items.length ? items.join('\n') : '—';
    const inLines = items.slice(0, 3);

    const runtime = enableRunState ? runtimeStepById?.[n.id] : undefined;
    const hasRuntime = Boolean(enableRunState && runtime && (runtime.last_error || runtime.output || runtime.status));
    if (hasRuntime) {
      const err = String(runtime?.last_error || '').trim();
      if (err) {
        const outTitle = `error: ${err}`;
        const outLines = [`error: ${err}`];
        return { inLines, inTitle, outLines, outTitle };
      }

      const out = runtime?.output;
      const base = normalizeRuntimeOutput(out);
      const obj = (base && typeof base === 'object' && !Array.isArray(base) && (base as any).result !== undefined)
        ? (typeof (base as any).result === 'object' && (base as any).result !== null ? (base as any).result : { result: (base as any).result })
        : base;

      const lines = toKvLines(obj);
      const outTitle = lines.length ? lines.join('\n') : (out !== undefined ? toCompactValue(base) : '—');
      const outLines = lines.length ? lines.slice(0, 3) : (out !== undefined ? [toCompactValue(base)] : []);
      return { inLines, inTitle, outLines, outTitle };
    }

    const outs = (stepOutputKeysByStepId?.[n.id] ?? []).slice();
    const outTitle = outs.length ? outs.join('\n') : '—';
    const outLines = outs.slice(0, 3);
    return { inLines, inTitle, outLines, outTitle };
  }, [enableRunState, resolveTemplateValue, runtimeStepById, stepOutputKeysByStepId, toCompactValue, toKvLines, tryParseJsonObject]);

  useSyncedHorizontalScroll({
    enabled: orientation === 'vertical',
    leftScrollRef,
    centerScrollRef,
    rightScrollRef,
    onScrollSync: scheduleWiringUpdate,
  });

  return (
    <div
      ref={containerRef}
      className={
        'pac-glass rounded-md h-full min-h-0 overflow-hidden relative '
        + (orientation === 'vertical' ? 'flex flex-col' : 'flex')
      }
    >
      <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
        {edgePaths.map((p) => (
          <path
            key={p.id}
            d={p.d}
            fill="none"
            stroke="currentColor"
            className="text-neutral-600"
            strokeWidth={2}
          />
        ))}
        {inputWiringPaths.map((p) => (
          <path
            key={p.id}
            d={p.d}
            fill="none"
            stroke="currentColor"
            className="text-neutral-700"
            strokeWidth={2}
          />
        ))}
        {outputWiringPaths.map((p) => (
          <path
            key={p.id}
            d={p.d}
            fill="none"
            stroke="currentColor"
            className="text-neutral-700"
            strokeWidth={2}
          />
        ))}
      </svg>

      {/* Condition markers (flowchart diamonds) */}
      <div className="absolute inset-0 pointer-events-none z-30">
        {edgeMidpoints.map((m) => {
          const label = whenLabelByEdgeId.get(m.id);
          if (!label) return null;
          const text = label.replace(/^◆\s*/, '');
          return (
            <div
              key={`cond-${m.id}`}
              className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center gap-1"
              style={{ left: m.x, top: m.y }}
              title={label}
            >
              <div className="w-2.5 h-2.5 rotate-45 border border-neutral-400 bg-neutral-950" />
              <div className="text-[10px] text-neutral-300 whitespace-nowrap">{text}</div>
            </div>
          );
        })}

        {inputWiringMidpoints.map((m) => {
          // id is `${inputKey}->${stepId}`
          const parts = String(m.id).split('->');
          const inputKey = (parts[0] ?? '').trim();
          const stepId = (parts[1] ?? '').trim();
          if (!inputKey || !stepId) return null;

          const requiredInputs = whenHasByStepId.get(stepId);
          if (!requiredInputs || !requiredInputs.has(inputKey)) return null;

          return (
            <div
              key={`cond-in-${m.id}`}
              className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center gap-1"
              style={{ left: m.x, top: m.y }}
              title={`Run condition\nhas: ${inputKey}`}
            >
              <div className="w-2.5 h-2.5 rotate-45 border border-neutral-400 bg-neutral-950" />
              <div className="text-[10px] text-neutral-300 whitespace-nowrap">{inputKey}</div>
            </div>
          );
        })}
      </div>

      {orientation === 'vertical' ? (
        <div className="shrink-0 border-b border-neutral-800 pac-glass-subtle">
          <div ref={leftScrollRef} className="w-full overflow-x-auto overflow-y-hidden p-2">
            <div className="text-[10px] text-neutral-400 mb-1">Inputs</div>
            {inputKeys.length === 0 ? (
              <div className="text-[10px] text-neutral-500">—</div>
            ) : (
              <div
                className="grid w-full"
                style={{
                  gridTemplateColumns: `repeat(${Math.max(1, inputKeys.length)}, minmax(0, 1fr))`,
                  columnGap: 8,
                }}
              >
                {inputKeys.map((k) => (
                  <div
                    key={k}
                    className="relative z-20 w-full"
                    ref={(el) => {
                      inputElByKeyRef.current.set(k, el);
                    }}
                  >
                    <IoCard name={k} meta={inputMetaByKey?.[k]} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="w-[120px] shrink-0 border-r border-neutral-800 pac-glass-subtle">
          <div ref={leftScrollRef} className="h-full overflow-auto p-2 space-y-3">
            <div>
              <div className="text-[10px] text-neutral-400 mb-1">Inputs</div>
              {inputKeys.length === 0 ? (
                <div className="text-[10px] text-neutral-500">—</div>
              ) : (
                <div className="space-y-2">
                  {inputKeys.map((k) => {
                    return (
                      <div
                        key={k}
                        className="relative z-20"
                        ref={(el) => {
                          inputElByKeyRef.current.set(k, el);
                        }}
                      >
                        <IoCard name={k} meta={inputMetaByKey?.[k]} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        ref={centerScrollRef}
        className={
          'flex-1 min-w-0 min-h-0 '
          + (orientation === 'vertical' ? 'overflow-y-auto overflow-x-hidden' : 'overflow-auto')
        }
      >
        {orientation === 'vertical' ? (
          <div className="w-full">
            <div className="flex flex-col" style={{ rowGap: 32, paddingTop: 32, paddingBottom: 32 }}>
              {nodesByLevel.map(({ level, nodes: levelNodes }) => {
                const cols = Math.max(1, levelNodes.length);
                return (
                  <div
                    key={level}
                    className="grid w-full"
                    style={{
                      gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                      columnGap: 0,
                    }}
                  >
                    {levelNodes.map((n) => {
                      const pos = layout.positions[n.id] || { x: 0, y: 0, level };
                      const selected = n.id === selectedStepId;
                      const boxLabel = selected && selectedStepLabel?.trim() ? selectedStepLabel.trim() : n.id;
                      const preview = getStepIoPreview(n);
                      const statusLabel = enableRunState ? getStatusLabelForStepId(n.id) : '';
                      const styles = enableRunState ? getNodeStatusStyles(statusLabel) : getNodeStatusStyles(undefined);
                      const isRunning = enableRunState && isRunningStatus(statusLabel);
                      const runtime = enableRunState ? runtimeStepById?.[n.id] : undefined;
                      const hasErr = Boolean(String(runtime?.last_error || '').trim());
                      const isError = enableRunState && (statusLabel === 'FAILED' || statusLabel === 'ERROR' || hasErr);
                      const isSuccess = enableRunState && !isError && statusLabel === 'SUCCEEDED';
                      const isDiscarded = enableRunState && statusLabel === 'DISCARDED';
                      const discardedText = '!text-neutral-600';
                      const textPrimary = isError ? 'text-rose-200' : (isSuccess ? 'text-emerald-200' : (isDiscarded ? discardedText : 'text-neutral-100'));
                      const textSecondary = isError ? 'text-rose-400' : (isSuccess ? 'text-emerald-400' : (isDiscarded ? discardedText : 'text-neutral-400'));
                      const textMeta = isError ? 'text-rose-500' : (isSuccess ? 'text-emerald-500' : (isDiscarded ? discardedText : 'text-neutral-500'));
                      const textValue = isError ? 'text-rose-200' : (isSuccess ? 'text-emerald-200' : (isDiscarded ? discardedText : 'text-neutral-300'));
                      const textDash = isError ? 'text-rose-500' : (isSuccess ? 'text-emerald-500' : (isDiscarded ? discardedText : 'text-neutral-500'));
                      const progress = stepProgressById?.[n.id] as any;
                      const progressLinesRaw = (enableRunState && isRunning)
                        ? ((stepProgressLinesById?.[n.id] as any) ?? [])
                        : [];
                      const externalLines = Array.isArray(progressLinesRaw) ? progressLinesRaw : [];
                      const localLines = (enableRunState && isRunning) ? (progressLogByStepIdRef.current.get(n.id) ?? []) : [];
                      const progressLines = (externalLines.length ? externalLines : localLines).slice(-1);
                      const progressText = (() => {
                        if (!enableRunState || !isRunning) return '';
                        if (!progress) return '';
                        const pct = (typeof progress.pct === 'number' ? progress.pct : (typeof progress.percent === 'number' ? progress.percent : null));
                        const msg = typeof progress.message === 'string' ? progress.message.trim() : '';
                        if (pct != null && msg) return `${pct}% ${msg}`;
                        if (pct != null) return `${pct}%`;
                        if (msg) return msg;
                        return '';
                      })();
                      const progressUpdatedAtMs = enableRunState ? (stepProgressUpdatedAtMsById?.[n.id] ?? null) : null;
                      const updatedAtMs = enableRunState ? (progressUpdatedAtMs ?? parseDateMs((runtime as any)?.updated_at)) : null;
                      const updatedAgeMs = (updatedAtMs != null) ? Math.max(0, nowTickMs - updatedAtMs) : null;
                      const updatedAgeLabel = (updatedAgeMs != null) ? formatAge(updatedAgeMs) : '';
                      const attempts = enableRunState ? (typeof (runtime as any)?.attempts === 'number' ? (runtime as any).attempts : null) : null;
                      const jobType = enableRunState ? String((runtime as any)?.job_type ?? '').trim() : '';
                      const jobId = enableRunState ? String((runtime as any)?.job_id ?? '').trim() : '';
                      const refreshingLine = (() => {
                        if (!enableRunState || !isRunning) return '';
                        if (progressText || progressLines.length) return '';
                        // If we have a per-step updated_at but no progress lines, prefer a more explicit “stalled” cue.
                        if (updatedAgeMs != null && updatedAgeMs >= 60_000) return `no progress for ${formatMsCompact(updatedAgeMs)}`;
                        const dots = (Math.floor(nowTickMs / 1000) % 3) + 1;
                        return `refreshing${'.'.repeat(dots)}`;
                      })();
                      const runningElapsed = (() => {
                        if (!enableRunState || !isRunning) return null;
                        const started = runningSinceMsByStepIdRef.current.get(n.id);
                        if (started == null) return null;
                        return Math.max(0, nowTickMs - started);
                      })();

                      return (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() => onSelect(n.id)}
                          ref={(el) => {
                            stepElByIdRef.current.set(n.id, el);
                          }}
                          className={
                            `relative overflow-hidden min-h-[148px] flex flex-col items-start justify-start text-left rounded border px-2 py-2 z-20 w-full `
                            + styles.container
                            + (selected ? ` ring-1 ${styles.ring}` : '')
                            + (isRunning ? vumeterBorderClass : '')
                            + (isDiscarded ? ' italic !text-neutral-600' : '')
                          }
                        >
                          <div className="relative z-10 w-full">
                            <div className="flex items-center justify-between">
                              <div className={`text-xs ${textSecondary}`}>{n.def.type}</div>
                              <div className="flex items-center gap-1">
                                {enableRunState ? (
                                  <span
                                    className={
                                      'text-[10px] font-semibold '
                                      + (isRunning ? vumeterTextClass.trim() : (isDiscarded ? '!text-neutral-600 italic' : getStatusTextClass(statusLabel)))
                                    }
                                  >
                                    {statusLabel}
                                  </span>
                                ) : null}
                                {enableRunState ? (() => {
                                  const fixed = isRunning
                                    ? runningElapsed
                                    : (completedDurationMsByStepIdRef.current.get(n.id) ?? extractDurationMs(runtimeStepById?.[n.id]?.output));
                                  if (fixed == null) return null;

                                  const colorClass = (() => {
                                    if (isRunning) return 'text-neutral-300';
                                    if (statusLabel === 'SUCCEEDED') return 'text-emerald-300';
                                    if (statusLabel === 'FAILED' || statusLabel === 'ERROR') return 'text-rose-300';
                                    return 'text-neutral-400';
                                  })();

                                  return (
                                    <div className={`text-[10px] tabular-nums whitespace-nowrap ${colorClass}`} title={isRunning ? 'running time' : 'duration'}>
                                      {formatMsCompact(fixed)}
                                    </div>
                                  );
                                })() : null}
                                <div className={`text-[10px] ${textMeta}`}>L{pos.level}</div>
                              </div>
                            </div>
                            <div className={`text-sm font-semibold truncate ${textPrimary}`}>{boxLabel}</div>
                            <div className={`text-[11px] truncate ${textSecondary}`}>{n.def.service || '— handler/service'}</div>

                            {enableRunState ? (
                              <div className={`mt-0.5 text-[10px] leading-snug ${textMeta}`}>
                                {attempts != null ? <span className="mr-2">attempts: <span className={textValue}>{attempts}</span></span> : null}
                                {jobType ? <span className="mr-2">job: <span className={textValue}>{jobType}</span></span> : null}
                                {updatedAgeLabel ? <span>updated: <span className={textValue}>{updatedAgeLabel}</span></span> : null}
                                {(!jobType && jobId) ? <span className="ml-2">job_id: <span className={textValue}>{jobId}</span></span> : null}
                              </div>
                            ) : null}

                            <div className="mt-1 text-[10px] leading-snug">
                              {progressLines.length ? (
                                <div className={`mb-0.5 break-words ${textSecondary}`}>
                                  {progressLines.map((line: string, idx: number) => (
                                    <div key={`${idx}:${line}`} title={line}>{line}</div>
                                  ))}
                                </div>
                              ) : ((refreshingLine || progressText) ? (
                                <div
                                  className={`mb-0.5 break-words ${textSecondary}`}
                                  title={refreshingLine || progressText}
                                >
                                  {refreshingLine || progressText}
                                </div>
                              ) : null)}
                              <div className={textMeta}>in:</div>
                              <div className={`pl-2 break-words ${textValue}`} title={preview.inTitle}>
                                {preview.inLines.length ? preview.inLines.map((line, idx) => (
                                  <div key={`${idx}:${line}`}>{line}</div>
                                )) : <div className={textDash}>—</div>}
                              </div>

                              <div className={`mt-1 ${textMeta}`}>out:</div>
                              <div className={`pl-2 break-words ${textValue}`} title={preview.outTitle}>
                                {preview.outLines.length ? preview.outLines.map((k, idx) => (
                                  <div key={`${idx}:${k}`}>{k}</div>
                                )) : <div className={textDash}>—</div>}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="relative" style={{ width: canvasW, height: canvasH }}>
            {nodes.map((n) => {
              const pos = layout.positions[n.id] || { x: 16, y: 16, level: 0 };
              const xByLevel = padding + pos.level * colWidth;
              const left = gutterW + xByLevel;
              const top = pos.y;
              const selected = n.id === selectedStepId;
              const boxLabel = selected && selectedStepLabel?.trim() ? selectedStepLabel.trim() : n.id;
              const preview = getStepIoPreview(n);
              const statusLabel = enableRunState ? getStatusLabelForStepId(n.id) : '';
              const styles = enableRunState ? getNodeStatusStyles(statusLabel) : getNodeStatusStyles(undefined);
              const isRunning = enableRunState && isRunningStatus(statusLabel);
              const runtime = enableRunState ? runtimeStepById?.[n.id] : undefined;
              const hasErr = Boolean(String(runtime?.last_error || '').trim());
              const isError = enableRunState && (statusLabel === 'FAILED' || statusLabel === 'ERROR' || hasErr);
              const isSuccess = enableRunState && !isError && statusLabel === 'SUCCEEDED';
              const isDiscarded = enableRunState && statusLabel === 'DISCARDED';
              const discardedText = '!text-neutral-600';
              const textPrimary = isError ? 'text-rose-200' : (isSuccess ? 'text-emerald-200' : (isDiscarded ? discardedText : 'text-neutral-100'));
              const textSecondary = isError ? 'text-rose-400' : (isSuccess ? 'text-emerald-400' : (isDiscarded ? discardedText : 'text-neutral-400'));
              const textMeta = isError ? 'text-rose-500' : (isSuccess ? 'text-emerald-500' : (isDiscarded ? discardedText : 'text-neutral-500'));
              const textValue = isError ? 'text-rose-200' : (isSuccess ? 'text-emerald-200' : (isDiscarded ? discardedText : 'text-neutral-300'));
              const textDash = isError ? 'text-rose-500' : (isSuccess ? 'text-emerald-500' : (isDiscarded ? discardedText : 'text-neutral-500'));
              const progress = stepProgressById?.[n.id] as any;
              const progressLinesRaw = (enableRunState && isRunning)
                ? ((stepProgressLinesById?.[n.id] as any) ?? [])
                : [];
              const externalLines = Array.isArray(progressLinesRaw) ? progressLinesRaw : [];
              const localLines = (enableRunState && isRunning) ? (progressLogByStepIdRef.current.get(n.id) ?? []) : [];
              const progressLines = (externalLines.length ? externalLines : localLines).slice(-1);
              const progressText = (() => {
                if (!enableRunState || !isRunning) return '';
                if (!progress) return '';
                const pct = (typeof progress.pct === 'number' ? progress.pct : (typeof progress.percent === 'number' ? progress.percent : null));
                const msg = typeof progress.message === 'string' ? progress.message.trim() : '';
                if (pct != null && msg) return `${pct}% ${msg}`;
                if (pct != null) return `${pct}%`;
                if (msg) return msg;
                return '';
              })();
              const progressUpdatedAtMs = enableRunState ? (stepProgressUpdatedAtMsById?.[n.id] ?? null) : null;
              const updatedAtMs = enableRunState ? (progressUpdatedAtMs ?? parseDateMs((runtime as any)?.updated_at)) : null;
              const updatedAgeMs = (updatedAtMs != null) ? Math.max(0, nowTickMs - updatedAtMs) : null;
              const updatedAgeLabel = (updatedAgeMs != null) ? formatAge(updatedAgeMs) : '';
              const attempts = enableRunState ? (typeof (runtime as any)?.attempts === 'number' ? (runtime as any).attempts : null) : null;
              const jobType = enableRunState ? String((runtime as any)?.job_type ?? '').trim() : '';
              const jobId = enableRunState ? String((runtime as any)?.job_id ?? '').trim() : '';
              const refreshingLine = (() => {
                if (!enableRunState || !isRunning) return '';
                if (progressText || progressLines.length) return '';
                if (updatedAgeMs != null && updatedAgeMs >= 60_000) return `no progress for ${formatMsCompact(updatedAgeMs)}`;
                const dots = (Math.floor(nowTickMs / 1000) % 3) + 1;
                return `refreshing${'.'.repeat(dots)}`;
              })();
              const runningElapsed = (() => {
                if (!enableRunState || !isRunning) return null;
                const started = runningSinceMsByStepIdRef.current.get(n.id);
                if (started == null) return null;
                return Math.max(0, nowTickMs - started);
              })();
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => onSelect(n.id)}
                  ref={(el) => {
                    stepElByIdRef.current.set(n.id, el);
                  }}
                  style={{ left, top }}
                  className={
                    `absolute relative overflow-hidden w-[170px] min-h-[148px] flex flex-col items-start justify-start text-left rounded border px-2 py-2 z-20 `
                    + styles.container
                    + (selected ? ` ring-1 ${styles.ring}` : '')
                    + (isRunning ? vumeterBorderClass : '')
                    + (isDiscarded ? ' italic !text-neutral-600' : '')
                  }
                >
                  <div className="relative z-10 w-full">
                    <div className="flex items-center justify-between">
                      <div className={`text-xs ${textSecondary}`}>{n.def.type}</div>
                      <div className="flex items-center gap-1">
                        {enableRunState ? (
                          <span
                            className={
                              'text-[10px] font-semibold '
                              + (isRunning ? vumeterTextClass.trim() : (isDiscarded ? '!text-neutral-600 italic' : getStatusTextClass(statusLabel)))
                            }
                          >
                            {statusLabel}
                          </span>
                        ) : null}
                        {enableRunState ? (() => {
                          const fixed = isRunning
                            ? runningElapsed
                            : (completedDurationMsByStepIdRef.current.get(n.id) ?? extractDurationMs(runtimeStepById?.[n.id]?.output));
                          if (fixed == null) return null;

                          const colorClass = (() => {
                            if (isRunning) return 'text-neutral-300';
                            if (statusLabel === 'SUCCEEDED') return 'text-emerald-300';
                            if (statusLabel === 'FAILED' || statusLabel === 'ERROR') return 'text-rose-300';
                            return 'text-neutral-400';
                          })();

                          return (
                            <div className={`text-[10px] tabular-nums whitespace-nowrap ${colorClass}`} title={isRunning ? 'running time' : 'duration'}>
                              {formatMsCompact(fixed)}
                            </div>
                          );
                        })() : null}
                        <div className={`text-[10px] ${textMeta}`}>L{pos.level}</div>
                      </div>
                    </div>
                    <div className={`text-sm font-semibold truncate ${textPrimary}`}>{boxLabel}</div>
                    <div className={`text-[11px] truncate ${textSecondary}`}>{n.def.service || '— handler/service'}</div>

                    {enableRunState ? (
                      <div className={`mt-0.5 text-[10px] leading-snug ${textMeta}`}>
                        {attempts != null ? <span className="mr-2">attempts: <span className={textValue}>{attempts}</span></span> : null}
                        {jobType ? <span className="mr-2">job: <span className={textValue}>{jobType}</span></span> : null}
                        {updatedAgeLabel ? <span>updated: <span className={textValue}>{updatedAgeLabel}</span></span> : null}
                        {(!jobType && jobId) ? <span className="ml-2">job_id: <span className={textValue}>{jobId}</span></span> : null}
                      </div>
                    ) : null}

                    <div className="mt-1 text-[10px] leading-snug">
                      {progressLines.length ? (
                        <div className={`mb-0.5 break-words ${textSecondary}`}>
                          {progressLines.map((line: string, idx: number) => (
                            <div key={`${idx}:${line}`} title={line}>{line}</div>
                          ))}
                        </div>
                      ) : ((refreshingLine || progressText) ? (
                        <div
                          className={`mb-0.5 break-words ${textSecondary}`}
                          title={refreshingLine || progressText}
                        >
                          {refreshingLine || progressText}
                        </div>
                      ) : null)}
                      <div className={textMeta}>in:</div>
                      <div className={`pl-2 break-words ${textValue}`} title={preview.inTitle}>
                        {preview.inLines.length ? preview.inLines.map((line) => (
                          <div key={line}>{line}</div>
                        )) : <div className={textDash}>—</div>}
                      </div>

                      <div className={`mt-1 ${textMeta}`}>out:</div>
                      <div className={`pl-2 break-words ${textValue}`} title={preview.outTitle}>
                        {preview.outLines.length ? preview.outLines.map((k) => (
                          <div key={k}>{k}</div>
                        )) : <div className={textDash}>—</div>}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {orientation === 'vertical' ? (
        <div className="shrink-0 border-t border-neutral-800 pac-glass-subtle">
          <div ref={rightScrollRef} className="w-full overflow-x-auto overflow-y-hidden p-2">
            <div className="text-[10px] text-neutral-400 mb-1">Outputs</div>
            {outputKeys.length === 0 ? (
              <div className="text-[10px] text-neutral-500">—</div>
            ) : (
              <div
                className="grid w-full"
                style={{
                  gridTemplateColumns: `repeat(${Math.max(1, outputKeys.length)}, minmax(0, 1fr))`,
                  columnGap: 8,
                }}
              >
                {outputKeys.map((k) => (
                  <div
                    key={k}
                    className="relative z-20 w-full"
                    ref={(el) => {
                      outputElByKeyRef.current.set(k, el);
                    }}
                  >
                    <IoCard name={k} meta={outputMetaByKey?.[k]} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="w-[120px] shrink-0 border-l border-neutral-800 pac-glass-subtle">
          <div ref={rightScrollRef} className="h-full overflow-auto p-2 space-y-3">
            <div>
              <div className="text-[10px] text-neutral-400 mb-1">Outputs</div>
              {outputKeys.length === 0 ? (
                <div className="text-[10px] text-neutral-500">—</div>
              ) : (
                <div className="space-y-2">
                  {outputKeys.map((k) => (
                    <div
                      key={k}
                      className="relative z-20"
                      ref={(el) => {
                        outputElByKeyRef.current.set(k, el);
                      }}
                    >
                      <IoCard name={k} meta={outputMetaByKey?.[k]} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
